import {
  faDownload,
  faTriangleExclamation
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useAtom } from 'jotai';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { PERMISSIONS } from '@/constants/auth.constants';
import useFetchOrganizationAssetTags from '@/hooks/useAssetTags';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import { useFetchAssetTypes } from '@/pages/Asset/Asset.store';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';

import useStyles from './AIResponseActionMatrix.styles';
import type { HostIsolationExclusions as HostIsolationExclusionsValue } from './AIResponseActionMatrix.types';
import {
  useDebouncedHostAssetSearchTerm,
  useHostIsolationAssetSearch
} from './HostIsolationExclusions.store';

import type {
  HostIsolationAssetOption,
  HostIsolationExclusionsHeadingProps,
  HostIsolationExclusionsProps
} from './HostIsolationExclusions.types';

const downloadHostExclusions = (hostNames: string[]) => {
  const objectUrl = URL.createObjectURL(
    new Blob([`host\n${hostNames.join('\n')}\n`], { type: 'text/csv' })
  );
  const link = document.createElement('a');

  try {
    link.href = objectUrl;
    link.download = 'host-isolation-exclusions.csv';
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    URL.revokeObjectURL(objectUrl);
  }
};

export const HostIsolationExclusionsHeading = ({
  exclusions,
  savedExclusions
}: HostIsolationExclusionsHeadingProps) => {
  const exclusionsChanged = !isEqual(exclusions, savedExclusions);
  const policyState =
    'When exclusions are configured, automatic host isolation is limited to hosts in your Asset Inventory. Unknown hosts will be skipped.';
  const policyDescription = `${exclusionsChanged ? 'Unsaved change. ' : ''}${policyState}`;

  return (
    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
      <Typography variant="h5">Host Isolation Exclusions</Typography>
      <Tooltip title={policyDescription}>
        <IconButton
          aria-label={policyDescription}
          size="small"
          sx={{ color: 'warning.main' }}
        >
          <FontAwesomeIcon icon={faTriangleExclamation} size="xs" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

const HostIsolationExclusions = ({
  disabled,
  exclusions,
  savedExclusions,
  organizationTags,
  organizationLabel,
  hostError,
  onHostErrorChange,
  onChange
}: HostIsolationExclusionsProps) => {
  const { classes } = useStyles();
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const hasAssetReadPermission = usePermissionsCheck(PERMISSIONS.ASSET_READ);
  const { data: assetTypes = {} } = useFetchAssetTypes();
  const { data: organizationAssetTags = [] } = useFetchOrganizationAssetTags(
    selectedOrganizationId
  );
  const [hostInput, setHostInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const debouncedHostInput = useDebouncedHostAssetSearchTerm(hostInput);
  const canSearchAssets =
    !disabled &&
    hasAssetReadPermission &&
    Boolean(organizationLabel) &&
    organizationLabel !== 'all';
  const {
    data: hostAssetSearch,
    error: hostAssetSearchError,
    fetchNextPage,
    hasNextPage,
    isError: isHostAssetSearchError,
    isFetching,
    isFetchingNextPage,
    refetch: retryHostAssetSearch
  } = useHostIsolationAssetSearch({
    enabled: canSearchAssets,
    organizationLabel,
    term: debouncedHostInput
  });

  useEffect(() => {
    setHostInput('');
  }, [disabled, organizationLabel]);

  const assetTypeOptions = useMemo(
    () =>
      Object.keys(
        assetTypes
      ) as HostIsolationExclusionsValue['excluded_asset_types'],
    [assetTypes]
  );
  const assetSubtypeOptions = useMemo(
    () =>
      [
        ...new Set(Object.values(assetTypes).flat())
      ].sort() as HostIsolationExclusionsValue['excluded_asset_subtypes'],
    [assetTypes]
  );
  const tagOptions = useMemo(() => {
    const names = [
      ...organizationTags,
      ...organizationAssetTags.map((tag) => tag.tag_name),
      ...exclusions.excluded_asset_tags
    ];
    return names.filter(
      (name, index) =>
        names.findIndex(
          (option) =>
            option.trim().toLocaleLowerCase() ===
            name.trim().toLocaleLowerCase()
        ) === index
    );
  }, [exclusions.excluded_asset_tags, organizationAssetTags, organizationTags]);

  const commitTagInput = () => {
    const tag = tagInput.trim();
    if (!tag) return;

    onChange({
      ...exclusions,
      excluded_asset_tags: [...exclusions.excluded_asset_tags, tag].filter(
        (value, index, tags) =>
          tags.findIndex(
            (candidate) =>
              candidate.trim().toLocaleLowerCase() ===
              value.trim().toLocaleLowerCase()
          ) === index
      )
    });
    setTagInput('');
  };

  const hostOptions = useMemo(() => {
    if (!canSearchAssets || hostInput.trim() !== debouncedHostInput) return [];

    const seenAssetNames = new Set<string>();
    return (hostAssetSearch?.pages.flat() ?? []).filter((asset) => {
      const normalizedName = asset.asset_name.toLowerCase();
      if (seenAssetNames.has(normalizedName)) return false;
      seenAssetNames.add(normalizedName);
      return true;
    });
  }, [canSearchAssets, debouncedHostInput, hostAssetSearch?.pages, hostInput]);

  const selectedHostOptions = useMemo(
    () =>
      exclusions.excluded_host_names.map(
        (assetName, index): HostIsolationAssetOption => ({
          id: -(index + 1),
          asset_name: assetName
        })
      ),
    [exclusions.excluded_host_names]
  );

  const handleHostChange = (
    _: React.SyntheticEvent,
    values: HostIsolationAssetOption[]
  ) => {
    onChange({
      ...exclusions,
      excluded_host_names: values
        .map((asset) => asset.asset_name)
        .filter(
          (assetName, index, assetNames) =>
            assetNames.findIndex(
              (candidate) => candidate.toLowerCase() === assetName.toLowerCase()
            ) === index
        )
    });
    onHostErrorChange(null);
  };

  const handleHostListScroll = (event: React.UIEvent<HTMLElement>) => {
    const listbox = event.currentTarget;
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 1
    ) {
      void fetchNextPage();
    }
  };

  const hostSearchHelperText =
    'Search this organization’s existing asset names to add an exclusion.';
  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <HostIsolationExclusionsHeading
        exclusions={exclusions}
        savedExclusions={savedExclusions}
      />
      <Box className={classes.hostExclusionControls}>
        <Box className={classes.hostExclusionHosts}>
          <InputLabel htmlFor="excluded-hostnames">
            Excluded host names ({exclusions.excluded_host_names.length})
          </InputLabel>
          <Autocomplete
            id="excluded-hostnames"
            multiple
            limitTags={3}
            options={hostOptions}
            value={selectedHostOptions}
            inputValue={hostInput}
            disabled={disabled || !hasAssetReadPermission}
            filterOptions={(options) => options}
            getOptionKey={(option) => option.id}
            getOptionLabel={(option) => option.asset_name}
            isOptionEqualToValue={(option, value) =>
              option.id === value.id && option.id !== -1
            }
            loading={isFetching}
            loadingText="Searching organization assets…"
            noOptionsText={
              hostInput.trim()
                ? 'No matching organization assets'
                : 'Type an asset name to search'
            }
            slotProps={{ listbox: { onScroll: handleHostListScroll } }}
            onChange={handleHostChange}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') setHostInput(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search organization asset names"
                error={!!hostError}
                helperText={hostError ?? hostSearchHelperText}
              />
            )}
          />
          {canSearchAssets && isHostAssetSearchError && (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  onClick={() => {
                    void retryHostAssetSearch();
                  }}
                >
                  Retry
                </Button>
              }
            >
              Unable to search organization assets
              {hostAssetSearchError instanceof Error
                ? `: ${hostAssetSearchError.message}`
                : '.'}
            </Alert>
          )}
        </Box>
        <Box className={classes.hostExclusionSelect}>
          <InputLabel htmlFor="excluded-asset-types">
            Excluded asset types
          </InputLabel>
          <Autocomplete
            id="excluded-asset-types"
            multiple
            options={assetTypeOptions}
            value={exclusions.excluded_asset_types}
            disabled={disabled}
            onChange={(_, value) =>
              onChange({ ...exclusions, excluded_asset_types: value })
            }
            renderInput={(params) => <TextField {...params} />}
          />
        </Box>
        <Box className={classes.hostExclusionSelect}>
          <InputLabel htmlFor="excluded-asset-subtypes">
            Excluded asset subtypes
          </InputLabel>
          <Autocomplete
            id="excluded-asset-subtypes"
            multiple
            options={assetSubtypeOptions}
            value={exclusions.excluded_asset_subtypes}
            disabled={disabled}
            onChange={(_, value) =>
              onChange({ ...exclusions, excluded_asset_subtypes: value })
            }
            renderInput={(params) => <TextField {...params} />}
          />
        </Box>
        <Box className={classes.hostExclusionTags}>
          <InputLabel htmlFor="excluded-asset-tags">
            Excluded asset tags
          </InputLabel>
          <Autocomplete
            id="excluded-asset-tags"
            multiple
            freeSolo
            limitTags={3}
            options={tagOptions}
            value={exclusions.excluded_asset_tags}
            inputValue={tagInput}
            disabled={disabled}
            onChange={(_, value) => {
              onChange({
                ...exclusions,
                excluded_asset_tags: value
                  .map((tag) => tag.trim())
                  .filter(Boolean)
              });
              setTagInput('');
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') setTagInput(value);
            }}
            onBlur={commitTagInput}
            renderInput={(params) => (
              <TextField
                {...params}
                helperText="Enter a tag name or select an existing asset-associated tag."
              />
            )}
          />
        </Box>
        <Box className={classes.hostExclusionUpload}>
          <InputLabel aria-hidden className={classes.hostExclusionUploadLabel}>
            CSV controls
          </InputLabel>
          <Button
            disabled={disabled}
            variant="outlined"
            startIcon={<FontAwesomeIcon size="lg" icon={faDownload} />}
            onClick={() =>
              downloadHostExclusions(exclusions.excluded_host_names)
            }
          >
            Download CSV
          </Button>
        </Box>
      </Box>
    </Stack>
  );
};

export default HostIsolationExclusions;
